import { TreeItem as MuiTreeItem, useTreeItem } from '@mui/x-tree-view';
import { styled } from '@mui/material/styles';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { InsightFilterType } from '../../apis/PythonInsightsAPI';
import { MappedInsightType } from '../../queries/useInsightsQuery';
import { InsightSummarySkeleton, RenderInsightSummary } from '../InsightsSummary';
import useInsightsSummaryQuery, {
  GetInsightsSummaryQueryFnType,
  GetInsightsSummaryQueryKeyType,
} from '../../queries/useInsightsSummaryQuery';
import _ from 'lodash';
import HandleOperation from 'components/utils/HandleOperation';
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon';
import ArrowDownSLineIcon from 'remixicon-react/ArrowDownSLineIcon';
import { theme } from 'config/theme';
import { useParams } from 'react-router-dom';
import { ReactComponent as StarsIcon } from 'assets/icons/BlackStars.svg';
import InsightsAISummary from './InsightsAISummary';
import { useState } from 'react';

type InsightTreeItemProps = {
  directory: string[];
  expandedInsightSummaryIds: string[];
  filters: InsightFilterType[] | undefined;
  includeBWInsights: boolean;
  insight: MappedInsightType;
  groupBy: string;
  level: number;
  parentInsightTitle: string;
  searchKeyword: string;
  getInsightsSummaryQueryFn?: GetInsightsSummaryQueryFnType;
  getInsightsSummaryQueryKey?: GetInsightsSummaryQueryKeyType;
};

function InsightTreeItem({
  directory,
  expandedInsightSummaryIds,
  filters,
  includeBWInsights,
  insight,
  groupBy,
  level,
  parentInsightTitle,
  searchKeyword,
  getInsightsSummaryQueryFn,
  getInsightsSummaryQueryKey,
}: InsightTreeItemProps) {
  const canShowInsightsSummary = !insight.children || insight.children?.length === 0;
  const { expanded } = useTreeItem(insight.title);
  const { projectId = '' } = useParams();
  const {
    insightsSummary,
    isLoading: isInsightsSummaryLoading,
    isError: isInsightsSummaryError,
    refetch,
  } = useInsightsSummaryQuery({
    directory,
    filters: [...(filters || []), ...insight.insightSummaryFilters],
    groupBy: insight.insightSummaryFilters?.[0].filterName || '',
    includeBWInsights,
    novaSearchKeyWord: searchKeyword,
    projectId,
    shouldFetch: expanded && canShowInsightsSummary,
    getInsightsSummaryQueryFn,
    getInsightsSummaryQueryKey,
  });
  const [openInsightsSummary, setOpenInsightsSummary] = useState(false);
  const [showSummaryBtn, setShowSummaryBtn] = useState(false);

  return (
    <>
      <TreeItem
        key={insight.title}
        nodeId={insight.title}
        expandIcon={
          <IconButton size="small">
            <ArrowRightSLineIcon
              size={level ? 22 : 26}
              color={theme.palette.custom.darkJungleGreen}
            />
          </IconButton>
        }
        collapseIcon={
          <IconButton size="small">
            <ArrowDownSLineIcon
              size={level ? 22 : 26}
              color={theme.palette.custom.darkJungleGreen}
            />
          </IconButton>
        }
        sx={{
          '& .MuiTreeItem-group': { marginLeft: 0.75 },
        }}
        label={
          <>
            <TitleContainer
              onMouseEnter={() => setShowSummaryBtn(true)}
              onMouseLeave={() => setShowSummaryBtn(false)}
            >
              <Title
                sx={{
                  fontWeight: level > 1 ? 500 : 700,
                  fontSize: level ? 15 : 16,
                }}
              >
                {insight?.displayTitle || insight.title}
              </Title>
              {!!level && <DottedLine component="span" className="count-border" />}
              {!!level && showSummaryBtn && (
                <Button
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpenInsightsSummary(true);
                  }}
                  size="small"
                  startIcon={<StarsIcon width={18} height={18} />}
                  sx={(theme) => ({
                    color: theme.palette.custom.link,
                    mx: 1,
                  })}
                >
                  Summarize
                </Button>
              )}
              <Count title="Matched Insights">{insight.count}</Count>
            </TitleContainer>
          </>
        }
      >
        {Array.isArray(insight.children) && insight.children.length > 0
          ? insight.children.map((_insight) => (
              <InsightTreeItem
                key={_insight.title}
                expandedInsightSummaryIds={expandedInsightSummaryIds}
                insight={_insight}
                groupBy={groupBy}
                directory={[...directory, _insight.title]}
                level={level + 1}
                searchKeyword={searchKeyword}
                filters={filters}
                includeBWInsights={includeBWInsights}
                parentInsightTitle={parentInsightTitle}
                getInsightsSummaryQueryFn={getInsightsSummaryQueryFn}
                getInsightsSummaryQueryKey={getInsightsSummaryQueryKey}
              />
            ))
          : canShowInsightsSummary && (
              <Box
                sx={(theme) => ({
                  borderLeft: `1px solid ${theme.palette.custom.platinum}`,
                  ml: 1,
                  pl: 0.5,
                })}
              >
                <HandleOperation
                  isLoading={isInsightsSummaryLoading}
                  isError={isInsightsSummaryError}
                  data={insightsSummary}
                  skeltonCount={insight.count > 2 ? 2 : insight.count}
                  skelton={<InsightSummarySkeleton />}
                >
                  {insightsSummary.map((insightSummary) => (
                    <InsightSummaryContainer key={insightSummary.id} sx={{ mb: 0.5 }}>
                      <RenderInsightSummary
                        parentInsightTitle={parentInsightTitle}
                        searchKeyword={searchKeyword}
                        insightSummary={insightSummary}
                        expandedInsightSummaryIds={expandedInsightSummaryIds}
                        groupBy={groupBy}
                        onUpdateCallback={refetch}
                      />
                    </InsightSummaryContainer>
                  ))}
                </HandleOperation>
              </Box>
            )}
      </TreeItem>
      {openInsightsSummary && (
        <InsightsAISummary
          open={openInsightsSummary}
          setOpen={setOpenInsightsSummary}
          insight={insight}
        />
      )}
    </>
  );
}

const TreeItem = styled(MuiTreeItem)({
  padding: '4px 0',
  '& .MuiTreeItem-content': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiTreeItem-iconContainer': {
    marginRight: 8,
  },
});

const TitleContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginRight: 8,
  '&:hover .count-border': {
    borderBottom: `2px dotted ${theme.palette.custom.divider}`,
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.custom.tuna,
}));

const DottedLine = styled(Box)(({ theme }) => ({
  flex: 1,
  height: 12,
  margin: '0 4px',
  borderBottom: `2px dotted ${theme.palette.custom.background}`,
}));

const Count = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.secondary.main,
  marginRight: 8,
}));

export const InsightSummaryContainer = styled(Paper)(({ theme }) => ({
  overflow: 'hidden',
  boxShadow: 'none',
  padding: '4px 10px',
  borderRadius: 8,
  margin: '4px 8px 0',
  backgroundColor: theme.palette.custom.whiteSmoke,
  cursor: 'pointer',
  '& .copy-summary': {
    visibility: 'hidden',
  },
  '&:hover .copy-summary': {
    visibility: 'visible',
  },
}));

export const ArticleDate = styled(Typography)({
  fontSize: 12,
  fontWeight: 400,
});

export default InsightTreeItem;
